import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { AppState } from 'src/app/app.reducer';
import { CompanyRegistrationService } from 'src/app/auth/services/company-registration.service';
import { KycApiService, OcrRegistroData, FacialVerificationResponse } from 'src/app/auth/services/kyc-api.service';
import { GeolocationService } from 'src/app/auth/components/dinamic-signup/services/geolocation.service';
import { motivoEdadEnOcr } from 'src/app/shared/utils/edad-minima.util';

// Configuracion de captura de documentos
const CAPTURE_CONFIG = {
  targetWidth: 1920,
  minWidth: 1280,
  maxWidth: 2400,
  quality: 0.85,
  minQuality: 0.5,
  qualityStep: 0.05,
  maxFileSizeMB: 4.5,
  targetFileSizeMB: 2,
  format: 'image/jpeg' as const,
  documentAspectRatio: 1.586,
};

const CAMERA_CONFIGS = {
  highEnd: {
    width: { ideal: 1920, min: 1280 },
    height: { ideal: 1080, min: 720 },
  },
  midRange: {
    width: { ideal: 1280, min: 640 },
    height: { ideal: 720, min: 480 },
  },
  lowEnd: {
    width: { ideal: 640 },
    height: { ideal: 480 },
  },
};

const SELFIE_CAMERA_CONFIG = {
  facingMode: 'user',
  width: { ideal: 1280, min: 640 },
  height: { ideal: 720, min: 480 },
};

/**
 * Coincidencia mínima exigida entre la selfie y la foto del documento.
 *
 * Es una PUERTA, no una sugerencia. Antes existía una banda intermedia (30-80%) que
 * mostraba "será revisada manualmente" y dejaba avanzar, pero esa revisión no llegaba
 * a ningún operador, así que era un bypass.
 */
const SIMILARITY_THRESHOLD = 80;

/** Intentos permitidos antes de ofrecer ayuda por WhatsApp. */
const MAX_VERIFICATION_ATTEMPTS = 3;

/** WhatsApp de soporte (el mismo que usa el navbar del dashboard). */
const SUPPORT_WHATSAPP = '56965474761';

/**
 * Códigos cuyo mensaje del backend es más específico que la entrada local. El KYC
 * devuelve un texto distinto por cada motivo de rechazo de liveness; duplicarlos acá
 * crearía dos fuentes de verdad.
 */
const PREFER_BACKEND_MESSAGE = new Set(['SELFIE_SPOOF_DETECTED']);

const NATIONALITY_MAP: Record<string, string> = {
  'CHL': 'Chile',
  'CHILE': 'Chile',
  'CHILENO': 'Chile',
  'CHILENA': 'Chile',
  'VEN': 'Venezuela',
  'VENEZUELA': 'Venezuela',
  'VENEZOLANO': 'Venezuela',
  'VENEZOLANA': 'Venezuela',
  'ARG': 'Argentina',
  'ARGENTINA': 'Argentina',
  'ARGENTINO': 'Argentina',
  'BOL': 'Bolivia',
  'BOLIVIA': 'Bolivia',
  'BOLIVIANO': 'Bolivia',
  'BOLIVIANA': 'Bolivia',
  'BRA': 'Brasil',
  'BRASIL': 'Brasil',
  'BRASILEÑO': 'Brasil',
  'BRASILEÑA': 'Brasil',
  'COL': 'Colombia',
  'COLOMBIA': 'Colombia',
  'COLOMBIANO': 'Colombia',
  'COLOMBIANA': 'Colombia',
  'ECU': 'Ecuador',
  'ECUADOR': 'Ecuador',
  'ECUATORIANO': 'Ecuador',
  'ECUATORIANA': 'Ecuador',
  'PRY': 'Paraguay',
  'PARAGUAY': 'Paraguay',
  'PARAGUAYO': 'Paraguay',
  'PARAGUAYA': 'Paraguay',
  'PER': 'Perú',
  'PERU': 'Perú',
  'PERUANO': 'Perú',
  'PERUANA': 'Perú',
  'URY': 'Uruguay',
  'URUGUAY': 'Uruguay',
  'URUGUAYO': 'Uruguay',
  'URUGUAYA': 'Uruguay',
  'USA': 'Estados Unidos',
  'ESTADOUNIDENSE': 'Estados Unidos',
};

const OCR_ERROR_MESSAGES: Record<string, string> = {
  FILE_VALIDATION_ERROR: 'Formato de archivo no valido. Usa JPG o PNG.',
  FILE_SIZE_EXCEEDED: 'El archivo es muy grande. Maximo 10MB.',
  INVALID_FILE_TYPE: 'Tipo de archivo no soportado. Usa JPG o PNG.',
  FILE_REQUIRED: 'Debes seleccionar un archivo.',
  DOCUMENT_IS_PHOTOCOPY: 'Debes tomar una foto del documento fisico, no una fotocopia.',
  DOCUMENT_NOT_RECOGNIZED: 'No pudimos leer el documento. Asegurate de que este completo y visible.',
  DOCUMENT_QUALITY_TOO_LOW: 'La imagen no es clara. Toma otra foto con mejor iluminacion.',
  DOCUMENT_CENSORED: 'El documento parece estar censurado u oculto. Muestra el documento completo.',
  INVALID_DOCUMENT_COUNTRY: 'Este tipo de documento no corresponde. Usa tu cedula chilena.',
  INVALID_DOCUMENT_TYPE: 'Tipo de documento no reconocido. Usa tu cedula de identidad.',
  OCR_EXTRACTION_FAILED: 'No pudimos leer algunos datos. Toma otra foto mas nitida.',
  AWS_SERVICE_ERROR: 'Error del servidor. Intenta nuevamente.',
  AWS_CREDENTIALS_ERROR: 'Error de autenticacion del servidor. Intenta mas tarde.',
  AWS_TEXTRACT_ERROR: 'Error procesando el documento. Intenta nuevamente.',
  AWS_REKOGNITION_ERROR: 'Error analizando la imagen. Intenta nuevamente.',
  AWS_THROTTLING_ERROR: 'Servicio temporalmente ocupado. Espera unos segundos e intenta nuevamente.',
  AWS_TIMEOUT_ERROR: 'El servidor tardo demasiado. Intenta nuevamente.',
  NO_FACE_DETECTED_SOURCE: 'No pudimos detectar tu rostro en la selfie.',
  NO_FACE_DETECTED_TARGET: 'No se detecto rostro en la foto del documento.',
  FACE_VERIFICATION_ERROR: 'Error en la verificacion facial. Intenta nuevamente.',
  DOCUMENT_SCREEN_RECAPTURE:
    'Debes tomar una foto del documento fisico, no de una pantalla. No fotografies el documento desde otro celular o monitor.',
  SELFIE_SPOOF_DETECTED:
    'La selfie debe ser una foto tomada en el momento con la camara. No sirve fotografiar una pantalla, una foto impresa ni otra imagen.',
};

type Step1SubStep = 'intro' | 'start-verification' | 'capture-front' | 'capture-back' | 'capture-selfie' | 'form';

@Component({
  selector: 'app-company-registration',
  templateUrl: './company-registration.component.html',
  styleUrls: ['./company-registration.component.sass'],
})
export class CompanyRegistrationComponent implements OnInit, OnDestroy {
  @ViewChildren('fileInput') fileInputs!: QueryList<ElementRef>;
  @ViewChild('video') videoElement!: ElementRef<HTMLVideoElement>;

  currentStep = 1;

  // Step 1 sub-steps
  step1SubStep: Step1SubStep = 'intro';

  // Step 2 sub-steps
  step2SubStep: 1 | 2 = 1;

  // Camera
  showCameraModal = false;
  isReadyToCapture = false;
  cameraPurpose: 'document' | 'selfie' = 'document';
  private stream: MediaStream | null = null;

  // Captured images
  documentFrontImage = '';
  documentBackImage = '';
  selfieImage = '';
  currentImage = '';
  rotationAngle = 0;

  // OCR
  ocrData: any = null;
  ocrRawResponse: any = null;
  fotoPersonaFromOcr = '';
  ocrLoading = false;

  // Face verification
  faceVerificationStatus: 'idle' | 'analyzing' | 'success' | 'error' = 'idle';

  /** Intentos fallidos de verificacion facial en este paso. */
  faceAttempts = 0;
  faceSimilarity = 0;
  faceVerificationMessage = '';

  // Registro completado pero pendiente de verificación
  registrationCompleted = false;

  // Loading inicial — evita mostrar step de captura antes de cargar data
  initialLoading = true;

  /**
   * Hay un paso guardandose contra el backend.
   *
   * Sin esto, dos clics seguidos en "Continuar" disparaban dos guardados en
   * paralelo y cada uno avanzaba un paso: el usuario aterrizaba dos pantallas
   * mas adelante y se saltaba la del medio (tipicamente "Informacion del
   * negocio", que quedaba sin guardar y sin forma de volver salvo recargando).
   */
  isSavingStep = false;

  // Consentimiento biométrico (pantalla start-verification)
  biometricConsent = false;

  // Animación slide
  slideDirection: 'left' | 'right' = 'left';

  // S3 keys guardadas del backend para recargar imágenes
  savedS3Keys: { selfie?: string; documentFront?: string; documentBack?: string } = {};

  representanteLegalForm!: FormGroup;
  personaJuridicaForm: FormGroup;
  informacionNegocioForm: FormGroup;
  beneficiarioFinalForm: FormGroup;
  beneficiarioFinalForm2: FormGroup;
  representantesLegalesForm: FormGroup;
  documentacionForm: FormGroup;
  controlEfectivoForm: FormGroup;
  documento_frontal: File | null = null;
  documento_reverso: File | null = null;
  documento_frontal2: File | null = null;
  documento_reverso2: File | null = null;
  documento_frontal3: File | null = null;
  documento_reverso3: File | null = null;
  documento_frontal4: File | null = null;
  documento_reverso4: File | null = null;
  beneficiarioIsRepresentante = false;
  errorMessage: string = '';
  successMessage: string = '';
  showDropdown = false;
  selectedCode = '';
  selectedFlag = '';
  porcentajes: number[] = Array.from({ length: 100 }, (_, i) => i + 1);
  errorTimeout: any;
  beneficiarios: any[] = [];
  representantes: any[] = [];
  controlEfectivo: any[] = [];
  filePreviews: { [key: string]: string | null } = {};
  extraFieldCounter = 0;
  @ViewChild('selfieInput') selfieInput!: ElementRef<HTMLInputElement>;
  generatedCode: string;

  tiposEmpresa: string[] = [
    'Empresa Individual de Responsabilidad Limitada',
    'Sociedad Anónima (abierta o cerrada)',
    'Sociedad Colectiva (Civil o en Comandita Civil)',
    'Sociedad de Responsabilidad Limitada',
    'Sociedad por Acciones',
    'Otro tipo de empresa',
  ];

  selectedTipo: string | null = null;

  documentosBase = [
    { label: 'Subir escritura constitución', control: 'escritura' },
    { label: 'Subir certificado vigencia', control: 'vigencia' },
    { label: 'Subir carpeta tributaria', control: 'carpetaTributaria' },
    {
      label: 'Subir Inscripción Conservador Bienes Raíces',
      control: 'conservador',
    },
    { label: 'Subir Publicación Diario Oficial', control: 'diarioOficial' },
  ];
  documentosRequeridos = [...this.documentosBase];

  countryPhoneOptions = [
    { code: '+56', name: 'Chile', flag: 'chile.png', iso: 'CHL' },
    { code: '+58', name: 'Venezuela', flag: 'venezuela.png', iso: 'VEN' },
    { code: '+54', name: 'Argentina', flag: 'argentina.png', iso: 'ARG' },
    { code: '+55', name: 'Brasil', flag: 'brasil.png', iso: 'BRA' },
    { code: '+57', name: 'Colombia', flag: 'colombia.png', iso: 'COL' },
    { code: '+593', name: 'Ecuador', flag: 'ecuador.png', iso: 'ECU' },
    { code: '+595', name: 'Paraguay', flag: 'paraguay.png', iso: 'PRY' },
    { code: '+51', name: 'Perú', flag: 'peru.png', iso: 'PER' },
    { code: '+598', name: 'Uruguay', flag: 'uruguay.png', iso: 'URY' },
    { code: '+1', name: 'Estados Unidos', flag: 'usa.png', iso: 'USA' },
  ];
  openBeneficiaryForm: any;
  openRepresentanteForm: any;
  openControlEfectivo: any;

  selfieFormGroup!: FormGroup;
  selectedSelfie: File | null = null;
  selectedSelfiePreview: string | ArrayBuffer | null = null;
  public subscription: Subscription = new Subscription();
  public user: any;
  public userId: string | undefined = '';
  private dataForms: any;

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private store: Store<AppState>,
    private companyService: CompanyRegistrationService,
    private kycService: KycApiService,
    private geolocationService: GeolocationService
  ) {
    this.subscription.add(
      this.store.select('auth').subscribe(({ user }: any) => {
        this.user = user;
        this.userId = user?.usuarioId;
      })
    );
    this.representanteLegalForm = this.fb.group({
      nombres: [{ value: '', disabled: true }, Validators.required],
      apellidos: [{ value: '', disabled: true }, Validators.required],
      rut: [{ value: '', disabled: true }, [Validators.required, Validators.pattern(/^[a-zA-Z0-9.-]+$/)]],
      nacionalidad: ['', Validators.required],
      direccion_particular: ['', Validators.required],
      tipo_departamento: [''],
      comuna: ['', Validators.required],
      ciudad: ['', Validators.required],
      pais_direccion: ['', Validators.required],
      porcentaje_participacion: ['', Validators.required],
      fecha_vencimiento_dni: ['', Validators.required],
      es_pep: [null, Validators.required],
      pep_funcion: [''],
      pep_institucion: [''],
      pep_fecha_inicio: [''],
      pep_fecha_termino: [''],
    });
    this.personaJuridicaForm = this.fb.group({
      razonSocial: ['', [Validators.required]],
      nombreFantasia: ['', [Validators.required]],
      giroComercial: ['', [Validators.required]],
      fechaConstitucion: ['', [Validators.required]],
      rut: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9.-]+$/)]],
      telefonoCodigo: ['+56', [Validators.required]],
      telefono: [
        '',
        [Validators.required, Validators.pattern(/^[0-9]{7,15}$/)],
      ],
      pais: ['', [Validators.required]],
      ciudad: ['', [Validators.required]],
      comuna: ['', [Validators.required]],
      direccion: ['', [Validators.required]],
      oficina: [''],
      motivoRelacion2: ['', [Validators.required]],
    });
    this.informacionNegocioForm = this.fb.group({
      descripcionNegocio: [
        '',
        [Validators.required, Validators.maxLength(500)],
      ],
      descripcionOrigenFondos: [
        '',
        [Validators.required, Validators.maxLength(500)],
      ],
      origenFondos: ['', [Validators.required]],
      ingresosMensuales: ['', Validators.required],
      patrimonioTotal: ['', Validators.required],
      patrimonioFinanciero: ['', Validators.required],
      actividadEconomica: ['', [Validators.required]],
      cantidadOperacionesMensuales: ['', Validators.required],
      volumenOperacionesMensuales: ['', Validators.required],
    });
    this.beneficiarioFinalForm = this.fb.group({
      nombres: ['', Validators.required],
      apellidos: ['', Validators.required],
      rut: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9.-]+$/)]],
      nacionalidad: ['', Validators.required],
      porcentaje_participacion: ['', Validators.required],
      direccion_particular: ['', Validators.required],
      tipo_departamento: [''],
      comuna: ['', Validators.required],
      ciudad: ['', Validators.required],
      pais_direccion: ['', Validators.required],
      documento_frontal: [null, Validators.required],
      documento_reverso: [null, Validators.required],
      fecha_vencimiento_dni: ['', Validators.required],
      es_pep: [null, Validators.required],
      pep_funcion: [''],
      pep_institucion: [''],
      pep_fecha_inicio: [''],
      pep_fecha_termino: [''],
    });
    this.beneficiarioFinalForm2 = this.fb.group({
      nombres: ['', Validators.required],
      apellidos: ['', Validators.required],
      rut: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9.-]+$/)]],
      nacionalidad: ['', Validators.required],
      porcentaje_participacion: ['', Validators.required],
      direccion_particular: ['', Validators.required],
      tipo_departamento: [''],
      comuna: ['', Validators.required],
      ciudad: ['', Validators.required],
      pais_direccion: ['', Validators.required],
      documento_frontal: [null, Validators.required],
      documento_reverso: [null, Validators.required],
      fecha_vencimiento_dni: ['', Validators.required],
      es_pep: [''],
      pep_funcion: [''],
      pep_institucion: [''],
      pep_fecha_inicio: [''],
      pep_fecha_termino: [''],
    });
    // PEP es opcional en beneficiarioFinalForm2
    this.representantesLegalesForm = this.fb.group({
      nombres: ['', Validators.required],
      apellidos: ['', Validators.required],
      rut: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9.-]+$/)]],
      nacionalidad: ['', Validators.required],
      porcentaje_participacion: ['', Validators.required],
      direccion_particular: ['', Validators.required],
      tipo_departamento: [''],
      comuna: ['', Validators.required],
      ciudad: ['', Validators.required],
      pais_direccion: ['', Validators.required],
      documento_frontal: [null, Validators.required],
      documento_reverso: [null, Validators.required],
      fecha_vencimiento_dni: ['', Validators.required],
      es_pep: [''],
      pep_funcion: [''],
      pep_institucion: [''],
      pep_fecha_inicio: [''],
      pep_fecha_termino: [''],
    });
    // PEP es opcional en representantesLegalesForm
    this.controlEfectivoForm = this.fb.group({
      nombres: ['', Validators.required],
      apellidos: ['', Validators.required],
      rut: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9.-]+$/)]],
      nacionalidad: ['', Validators.required],
      porcentaje_participacion: ['', Validators.required],
      direccion_particular: ['', Validators.required],
      tipo_departamento: [''],
      comuna: ['', Validators.required],
      ciudad: ['', Validators.required],
      pais_direccion: ['', Validators.required],
      documento_frontal: [null, Validators.required],
      documento_reverso: [null, Validators.required],
      fecha_vencimiento_dni: ['', Validators.required],
      es_pep: [''],
      pep_funcion: [''],
      pep_institucion: [''],
      pep_fecha_inicio: [''],
      pep_fecha_termino: [''],
    });
    // PEP es opcional en controlEfectivoForm
    this.documentacionForm = this.fb.group({
      tipoEmpresa: ['', Validators.required],
      escritura: [null],
      vigencia: [null],
      carpetaTributaria: [null],
      conservador: [null],
      diarioOficial: [null],
    });

    const controlsConfig: { [key: string]: any } = {
      tipoEmpresa: ['', Validators.required],
    };

    for (const doc of this.documentosBase) {
      controlsConfig[doc.control] = [null];
    }

    this.documentacionForm = this.fb.group(controlsConfig);

    this.selfieFormGroup = this.fb.group({
      selfie: [null, Validators.required],
    });
    this.generatedCode = this.generateCode(6);
  }
  async ngOnInit(): Promise<void> {
    await this.delay(100);
    try {
      await this.waitForUserIdAndFetchData();
    } catch (error) {
    } finally {
      this.initialLoading = false;
    }
    this.autoDetectPhoneCode();
  }

  private autoDetectPhoneCode(): void {
    // Si ya hay datos cacheados, usarlos directamente
    const cached = this.geolocationService.cachedGeoData;
    if (cached?.detectedCountry) {
      this.applyDetectedCountry(cached.detectedCountry);
      return;
    }
    // Si no, suscribirse para obtenerlos
    this.subscription.add(
      this.geolocationService.getGeolocation().subscribe((geoData) => {
        if (geoData?.detectedCountry) {
          this.applyDetectedCountry(geoData.detectedCountry);
        }
      })
    );
  }

  private applyDetectedCountry(country: any): void {
    if (!this.selectedCode || this.selectedCode === '') {
      const match = this.countryPhoneOptions.find((c) => c.iso === country.iso);
      if (match) {
        this.selectedCode = match.code;
        this.selectedFlag = 'assets/flags/' + match.flag;
        this.personaJuridicaForm.get('telefonoCodigo')?.setValue(match.code);
      }
    }
  }
  async waitForUserIdAndFetchData(retries = 10, delayMs = 1000): Promise<void> {
    for (let attempt = 0; attempt < retries; attempt++) {
      if (this.userId) {
        try {
          this.dataForms = await this.companyService
            .getRegistration(this.userId)
            .then((res) => {
              this.populateFormsFromResponse(res.data.companyDocuments);
            });

          return;
        } catch (error) {
          throw error;
        }
      }
      await this.delay(delayMs);
    }
  }

  populateFormsFromResponse(response: any): void {
    if (response.isCompleted && this.user.EstadoCompliance === 3) {
      this.redirect('/wallet/home');
      return;
    }
    if (response.isCompleted) {
      // Registro completado pero aún no aprobado — mostrar pantalla de verificación en proceso
      this.registrationCompleted = true;
      return;
    }

    // Guardar s3Keys del backend para recargar imágenes después
    if (response?.data4?.s3Keys) {
      this.savedS3Keys = {
        selfie: response.data4.s3Keys.selfie || '',
        documentFront: response.data4.s3Keys.documentFront || '',
        documentBack: response.data4.s3Keys.documentBack || '',
      };
    }

    // Cargar representante legal desde data1 y/o data4
    if (response?.data1?._format === 'new_ocr') {
      const d = response.data1;
      // Primero cargar todos los campos de data1
      this.representanteLegalForm.patchValue({
        nombres: d.nombres || '',
        apellidos: d.apellidos || '',
        rut: d.rut || '',
        nacionalidad: d.nacionalidad || '',
        direccion_particular: d.direccion_particular || '',
        tipo_departamento: d.tipo_departamento || '',
        comuna: d.comuna || '',
        ciudad: d.ciudad || '',
        pais_direccion: d.pais_direccion || '',
        porcentaje_participacion: d.porcentaje_participacion || '',
        fecha_vencimiento_dni: d.fecha_vencimiento_dni || '',
        es_pep: d.es_pep !== undefined && d.es_pep !== null && d.es_pep !== '' ? d.es_pep : '',
        pep_funcion: d.pep_funcion || '',
        pep_institucion: d.pep_institucion || '',
        pep_fecha_inicio: d.pep_fecha_inicio || '',
        pep_fecha_termino: d.pep_fecha_termino || '',
      });

      // Si data1 no tiene OCR (guardado viejo), intentar leer de data4
      if (!d.nombres && !d.apellidos && response?.data4?.ocr?.campos) {
        const campos = response.data4.ocr.campos;
        if (campos.nombres || campos.apellidos) {
          this.representanteLegalForm.patchValue({
            nombres: campos.nombres || '',
            apellidos: campos.apellidos || '',
            rut: campos.run || campos.runSinFormato || '',
            nacionalidad: this.mapNationality(campos.nacionalidad) || d.nacionalidad || '',
          });
          if (campos.fechaVencimientoISO && !d.fecha_vencimiento_dni) {
            this.representanteLegalForm.patchValue({
              fecha_vencimiento_dni: campos.fechaVencimientoISO,
            });
          }
        }
      }

      this.step1SubStep = 'form';
    } else if (response?.data4?.s3Keys) {
      // data1 no tiene formato new_ocr, leer OCR desde data4
      const campos = response.data4.ocr?.campos || {};
      this.representanteLegalForm.patchValue({
        nombres: campos.nombres || '',
        apellidos: campos.apellidos || '',
        rut: campos.run || campos.runSinFormato || '',
        nacionalidad: this.mapNationality(campos.nacionalidad) || '',
      });
      if (campos.fechaVencimientoISO) {
        this.representanteLegalForm.patchValue({
          fecha_vencimiento_dni: campos.fechaVencimientoISO,
        });
      }
      if (response?.data1) {
        this.representanteLegalForm.patchValue({
          direccion_particular: response.data1.direccion_particular || '',
          tipo_departamento: response.data1.tipo_departamento || '',
          comuna: response.data1.comuna || '',
          ciudad: response.data1.ciudad || '',
          pais_direccion: response.data1.pais_direccion || '',
          porcentaje_participacion: response.data1.porcentaje_participacion || '',
        });
      }
      this.step1SubStep = 'form';
    } else if (response?.data4?.nombres) {
      // FORMATO VIEJO: campos planos en data4
      this.representanteLegalForm.patchValue({
        nombres: response.data4.nombres || '',
        apellidos: response.data4.apellidos || '',
        rut: response.data4.rut || '',
        nacionalidad: response.data4.nacionalidad || '',
        fecha_vencimiento_dni: response.data4.fecha_vencimiento_dni || '',
      });
      this.step1SubStep = 'form';
    }

    // Cargar PEP desde data6 (solo formato viejo — en new_ocr ya viene en data1)
    if (response?.data1?._format !== 'new_ocr') {
      if (response?.data6?.es_pep !== undefined) {
        this.representanteLegalForm.patchValue({
          es_pep: response.data6.es_pep !== undefined && response.data6.es_pep !== null && response.data6.es_pep !== '' ? response.data6.es_pep : '',
          pep_funcion: response.data6.pep_funcion || '',
          pep_institucion: response.data6.pep_institucion || '',
          pep_fecha_inicio: response.data6.pep_fecha_inicio || '',
          pep_fecha_termino: response.data6.pep_fecha_termino || '',
        });
      } else if (response?.data4?.es_pep !== undefined) {
        this.representanteLegalForm.patchValue({
          es_pep: response.data4.es_pep !== undefined && response.data4.es_pep !== null && response.data4.es_pep !== '' ? response.data4.es_pep : '',
          pep_funcion: response.data4.pep_funcion || '',
          pep_institucion: response.data4.pep_institucion || '',
          pep_fecha_inicio: response.data4.pep_fecha_inicio || '',
          pep_fecha_termino: response.data4.pep_fecha_termino || '',
        });
      }
    }

    if (response?.data2) {
      const telefonoCodigo = response.data2.telefonoCodigo || '+56';

      this.personaJuridicaForm.patchValue({
        razonSocial: response.data2.razonSocial || '',
        nombreFantasia: response.data2.nombreFantasia || '',
        giroComercial: response.data2.giroComercial || '',
        fechaConstitucion: response.data2.fechaConstitucion || '',
        rut: response.data2.rut || '',
        telefonoCodigo: telefonoCodigo,
        telefono: response.data2.telefono || '',
        pais: response.data2.pais || '',
        ciudad: response.data2.ciudad || '',
        comuna: response.data2.comuna || '',
        direccion: response.data2.direccion || '',
        oficina: response.data2.oficina || '',
        motivoRelacion2: response.data2.motivoRelacion2 || '',
      });
      const foundCountry = this.countryPhoneOptions.find(
        (c) => c.code === response.data2.telefonoCodigo
      );

      if (foundCountry) {
        this.selectedCode = foundCountry.code;
        this.selectedFlag = 'assets/flags/' + foundCountry.flag;
        this.personaJuridicaForm
          .get('telefonoCodigo')
          ?.setValue(foundCountry.code);
      }
    }

    if (response?.data3) {
      this.informacionNegocioForm.patchValue({
        descripcionNegocio: response.data3.descripcionNegocio || '',
        descripcionOrigenFondos: response.data3.descripcionOrigenFondos || '',
        origenFondos: response.data3.origenFondos || '',
        ingresosMensuales: response.data3.ingresosMensuales || '',
        patrimonioTotal: response.data3.patrimonioTotal || '',
        patrimonioFinanciero: response.data3.patrimonioFinanciero || '',
        actividadEconomica: response.data3.actividadEconomica || '',
        cantidadOperacionesMensuales:
          response.data3.cantidadOperacionesMensuales || '',
        volumenOperacionesMensuales:
          response.data3.volumenOperacionesMensuales || '',
      });
    }

    if (response?.data5) {
      this.beneficiarios = response.data5.beneficiarios || [];
      this.representantes = response.data5.representantes || [];
      this.controlEfectivo = response.data5.controlEfectivo || [];
    }

    // Restaurar currentStep basándose en el progreso guardado.
    //
    // El paso 7 exige que step5 este completo, no solo step6: el backend
    // rechaza `step/7` si falta cualquiera de los seis anteriores. El
    // pre-registro nuevo de `dinamic-signup` escribe step 1, 2, 3, 4 y 6 y
    // nunca el 5, asi que preguntar solo por step6 lo depositaba en la
    // pantalla de confirmacion final —salteando beneficiario final y socios— y
    // ahi el envio fallaba siempre. Un registro hecho todo en el monolito no
    // cambia: recorre los pasos en orden, y si tiene el 6 tiene el 5.
    if (response?.progress) {
      const p = response.progress;
      if (p.step7?.completed) {
        this.currentStep = 7;
      } else if (p.step6?.completed && p.step5?.completed) {
        this.currentStep = 7;
      } else if (p.step5?.completed) {
        this.currentStep = 6;
      } else if (p.step4?.completed && p.step3?.completed) {
        // Step 4 completado = representante legal (nuevo formato)
        // Si hay beneficiarios guardados (data5), ir a step 5
        // Si no, ir a step 4 (formulario beneficiario final)
        if (this.beneficiarios.length > 0) {
          this.currentStep = 5;
        } else {
          this.currentStep = 4;
        }
      } else if (p.step3?.completed) {
        this.currentStep = 4;
      } else if (p.step2?.completed) {
        this.currentStep = 3;
      } else if (p.step1?.completed) {
        this.currentStep = 2;
      }
    }

    // Pre-cargar imágenes del step 1 para que estén disponibles en beneficiario y socios
    this.reloadStep1Images().then(() => {
    });

    // Auto-poblar representantes y control efectivo con datos del rep legal + beneficiarios
    this.autoPopulateStep5();
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async nextStep() {
    if (this.isSavingStep) return;
    this.isSavingStep = true;
    try {
      await this.runNextStep();
    } finally {
      this.isSavingStep = false;
    }
  }

  private async runNextStep() {
    let uId = this.userId || '';

    if (this.documentacionForm.valid && this.currentStep === 6) {
      await this.uploadAllFiles();
    }
    if (
      this.beneficiarios.length > 0 &&
      this.representantes.length > 0 &&
      this.currentStep === 5
    ) {
      let body = {
        beneficiarios: this.beneficiarios,
        representantes: this.representantes,
        controlEfectivo: this.controlEfectivo,
      };
      await this.submitStepAndAdvance(5, body, uId);
    }
    if (this.beneficiarioFinalForm.valid && this.currentStep === 4) {
      const newBeneficiary = {
        ...this.beneficiarioFinalForm.value,
        id: this.beneficiarios.length + 1,
      };
      this.beneficiarios.push(newBeneficiary);

      await this.uploadBeneficiarioFiles();

      this.beneficiarioFinalForm.reset();
      this.documento_frontal = null;
      this.documento_reverso = null;

      this.goToStep(5);
      this.autoPopulateStep5();
    }
    if (this.informacionNegocioForm.valid && this.currentStep === 3) {
      const guardado = await this.submitStepAndAdvance(
        3,
        this.informacionNegocioForm.getRawValue(),
        uId
      );
      // Solo se sigue si el backend acepto los datos. Antes se avanzaba igual
      // cuando el POST fallaba, asi que la pantalla cambiaba y "Informacion del
      // negocio" se perdia en silencio.
      if (guardado) {
        // Si el beneficiario final ya estaba cargado (registro que se retoma),
        // el paso 4 es justo el formulario para cargarlo: no se vuelve a pedir.
        if (this.beneficiarios.length > 0) {
          this.goToStep(5);
        }
        this.autoPopulateStep5();
      }
    }
    if (this.personaJuridicaForm.valid && this.currentStep === 2 && this.step2SubStep === 2) {
      // Nota: La validación de RUT empresa se hace en el backend durante la consolidación (paso 7)
      // Aquí solo enviamos los datos sin validar, ya que el backend tiene la lógica correcta
      await this.submitStepAndAdvance(
        2,
        this.personaJuridicaForm.getRawValue(),
        uId
      );
    }
    if (this.currentStep === 1 && this.step1SubStep === 'form') {
      await this.submitStep1();
    }
  }

  prevStep() {
    if (this.currentStep === 2 && this.step2SubStep === 2) {
      this.step2SubStep = 1;
      return;
    }
    this.goToStep(this.currentStep - 1, 'right');
  }

  nextStep2SubStep(): void {
    this.step2SubStep = 2;
  }

  goToStep(step: number, direction: 'left' | 'right' = 'left'): void {
    if (step === this.currentStep) return;
    this.slideDirection = direction;
    this.currentStep = step;
  }

  prevStep2SubStep(): void {
    this.step2SubStep = 1;
  }
  async uploadSelfie(): Promise<void> {
    const formEntries = Object.entries(this.selfieFormGroup.value);
    let json: any = {};
    for (const [key, value] of formEntries) {
      if (!(value instanceof File)) continue;

      try {
        const base64 = await this.convertFileToBase64(value);
        let data = await this.companyService.sendFile({
          name: key,
          base64: base64,
          id: this.user.cognitoId,
        });
        json[key] = data.key;
      } catch (error) {
      }
    }
    await this.submitStepAndAdvance(7, json, this.userId ? this.userId : '');
  }

  async uploadAllFiles(): Promise<void> {
    const formEntries = Object.entries(this.documentacionForm.value);
    let json: any = {};
    for (const [key, value] of formEntries) {
      if (!(value instanceof File)) continue;

      try {
        const base64 = await this.convertFileToBase64(value);
        let data = await this.companyService.sendFile({
          name: key,
          base64: base64,
          id: this.user.cognitoId,
        });
        json[key] = data.key;
      } catch (error) {
      }
    }
    // Incluir datos PEP del representante legal (el backend lee es_pep desde data6)
    json['es_pep'] = this.representanteLegalForm.get('es_pep')?.value || '';
    json['pep_funcion'] = this.representanteLegalForm.get('pep_funcion')?.value || '';
    json['pep_institucion'] = this.representanteLegalForm.get('pep_institucion')?.value || '';
    json['pep_fecha_inicio'] = this.representanteLegalForm.get('pep_fecha_inicio')?.value || '';
    json['pep_fecha_termino'] = this.representanteLegalForm.get('pep_fecha_termino')?.value || '';

    await this.submitStepAndAdvance(6, json, this.userId ? this.userId : '');
  }
  async uploadBeneficiarioFiles(): Promise<void> {
    const rut = this.beneficiarioFinalForm.get('rut')?.value || '';
    const uploads = [
      { file: this.documento_frontal, name: `documento_frontal${rut}BeneficiarioFinal` },
      { file: this.documento_reverso, name: `documento_reverso${rut}BeneficiarioFinal` },
    ];
    for (const { file, name } of uploads) {
      if (!file) continue;
      try {
        const base64 = await this.convertFileToBase64(file);
        await this.companyService.sendFile({
          name,
          base64,
          id: this.user.cognitoId,
        });
      } catch (error) {
      }
    }
  }

  async uploadControlEfectivoFiles(): Promise<void> {
    const rut = this.controlEfectivoForm.get('rut')?.value || '';
    const uploads = [
      { file: this.documento_frontal4, name: `documento_frontal${rut}controlEfectivo` },
      { file: this.documento_reverso4, name: `documento_reverso${rut}controlEfectivo` },
    ];
    for (const { file, name } of uploads) {
      if (!file) continue;
      try {
        const base64 = await this.convertFileToBase64(file);
        await this.companyService.sendFile({
          name,
          base64,
          id: this.user.cognitoId,
        });
      } catch (error) {
      }
    }
  }

  async uploadRepresentanteFiles(): Promise<void> {
    const rut = this.representantesLegalesForm.get('rut')?.value || '';
    const uploads = [
      { file: this.documento_frontal3, name: `documento_frontal${rut}representanteLegal` },
      { file: this.documento_reverso3, name: `documento_reverso${rut}representanteLegal` },
    ];
    for (const { file, name } of uploads) {
      if (!file) continue;
      try {
        const base64 = await this.convertFileToBase64(file);
        await this.companyService.sendFile({
          name,
          base64,
          id: this.user.cognitoId,
        });
      } catch (error) {
      }
    }
  }

  /**
   * Guarda un paso y avanza a la pantalla siguiente. Devuelve `true` si el
   * backend acepto los datos.
   *
   * El destino sale del paso que se guardo (`step + 1`), no de `currentStep`:
   * asi el avance es idempotente y dos guardados del mismo paso dejan al
   * usuario en la misma pantalla en vez de correrlo dos veces.
   */
  async submitStepAndAdvance(
    step: number,
    data: any,
    id: string
  ): Promise<boolean> {
    try {
      const response = await this.companyService.addDataToRegister(
        step,
        data,
        id
      );

      if (!response) return false;

      this.goToStep(step + 1);
      this.errorMessage = '';
      return true;
    } catch (error: any) {
      // Mostrar mensaje de error al usuario
      this.errorMessage = error?.error?.message || error?.message || 'Error al guardar los datos. Por favor intente nuevamente.';

      // Limpiar el mensaje después de 6 segundos
      clearTimeout(this.errorTimeout);
      this.errorTimeout = setTimeout(() => (this.errorMessage = ''), 6000);
      return false;
    }
  }

  onFileSelected(event: any, type: 'documento_frontal' | 'documento_reverso') {
    const file = event.target.files[0];
    if (file) {
      if (type === 'documento_frontal') {
        this.documento_frontal = file;
      } else {
        this.documento_reverso = file;
      }
      this.beneficiarioFinalForm
        .get(type)
        ?.setValue(
          this.user.cognitoId +
            '/' +
            type +
            this.beneficiarioFinalForm.get('rut')?.value +
            'BeneficiarioFinal.png'
        );
    }
  }
  async processAndSendFiles() {
    const formValue = this.documentacionForm.value;

    for (const key of Object.keys(formValue)) {
      const value = formValue[key];

      if (value instanceof File) {
        try {
          const base64 = await this.convertFileToBase64(value);

          await this.companyService.sendFile({
            name: key,
            base64: base64,
            id: this.userId,
          });
        } catch (err) {
        }
      }
    }
  }

  convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  }
  onFileSelected2(event: any, type: 'documento_frontal' | 'documento_reverso') {
    const file = event.target.files[0];
    if (file) {
      if (type === 'documento_frontal') {
        this.documento_frontal2 = file;
      } else {
        this.documento_reverso2 = file;
      }
      this.beneficiarioFinalForm2
        .get(type)
        ?.setValue(
          this.user.cognitoId +
            '/' +
            type +
            this.beneficiarioFinalForm2.get('rut')?.value +
            'BeneficiarioFinal.png'
        );
    }
  }
  onFileSelected3(event: any, type: 'documento_frontal' | 'documento_reverso') {
    const file = event.target.files[0];
    if (file) {
      if (type === 'documento_frontal') {
        this.documento_frontal3 = file;
      } else {
        this.documento_reverso3 = file;
      }
      this.representantesLegalesForm
        .get(type)
        ?.setValue(
          this.user.cognitoId +
            '/' +
            type +
            this.representantesLegalesForm.get('rut')?.value +
            'representanteLegal.png'
        );
    }
  }
  onFileSelected4(event: any, type: 'documento_frontal' | 'documento_reverso') {
    const file = event.target.files[0];
    if (file) {
      if (type === 'documento_frontal') {
        this.documento_frontal4 = file;
      } else {
        this.documento_reverso4 = file;
      }
      this.controlEfectivoForm
        .get(type)
        ?.setValue(
          this.user.cognitoId +
            '/' +
            type +
            this.controlEfectivoForm.get('rut')?.value +
            'controlEfectivo.png'
        );
    }
  }
  selectPhone(code: string, flag: string) {
    this.selectedCode = code;
    this.selectedFlag = `assets/flags/${flag}`;
    this.personaJuridicaForm.get('telefonoCodigo')?.setValue(code);
    this.showDropdown = false;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const clickedInside = target.closest('.phone-group');
    if (!clickedInside) {
      this.showDropdown = false;
    }
  }

  allowOnlyNumbers(event: KeyboardEvent) {
    const inputChar = String.fromCharCode(event.keyCode);
    if (!/^[0-9]$/.test(inputChar)) {
      event.preventDefault();
    }
  }

  showErrorPersonaJuridicaForm(field: string) {
    const control = this.personaJuridicaForm.get(field);

    if (control && control.invalid && control.touched) {
      this.errorMessage = this.getErrorMessage(field);
    } else {
      return;
    }

    clearTimeout(this.errorTimeout);
    this.errorTimeout = setTimeout(() => (this.errorMessage = ''), 4000);
  }

  getErrorMessage(field: string): string {
    const messages: Record<string, string> = {
      telefono: 'Ingresa un número de WhatsApp válido (solo números).',
    };
    return messages[field] || 'Campo no válido.';
  }

  onPepToggle(): void {
    const currentValue = this.beneficiarioFinalForm.get('es_pep')?.value;
    this.beneficiarioFinalForm.get('es_pep')?.setValue(!currentValue);
  }

  setRepresentantePep(isPep: boolean): void {
    this.representanteLegalForm.get('es_pep')?.setValue(isPep);
  }

  setBeneficiarioPep(isPep: boolean): void {
    this.beneficiarioFinalForm.get('es_pep')?.setValue(isPep);
  }

  async prefillBeneficiarioFromOcr(): Promise<void> {
    const repValues = this.representanteLegalForm.getRawValue();
    if (!repValues.nombres && !repValues.apellidos) return;

    this.beneficiarioFinalForm.patchValue({
      nombres: repValues.nombres || '',
      apellidos: repValues.apellidos || '',
      rut: repValues.rut || '',
      nacionalidad: repValues.nacionalidad || '',
      fecha_vencimiento_dni: repValues.fecha_vencimiento_dni || '',
      direccion_particular: repValues.direccion_particular || '',
      tipo_departamento: repValues.tipo_departamento || '',
      comuna: repValues.comuna || '',
      ciudad: repValues.ciudad || '',
      pais_direccion: repValues.pais_direccion || '',
      porcentaje_participacion: repValues.porcentaje_participacion || '',
      es_pep: repValues.es_pep !== '' && repValues.es_pep !== null && repValues.es_pep !== undefined
        ? repValues.es_pep : null,
      pep_funcion: repValues.pep_funcion || '',
      pep_institucion: repValues.pep_institucion || '',
      pep_fecha_inicio: repValues.pep_fecha_inicio || '',
      pep_fecha_termino: repValues.pep_fecha_termino || '',
    });

    // Si las imágenes no están en memoria (recarga de página), recargarlas
    if (!this.documentFrontImage || !this.documentBackImage) {
      await this.reloadStep1Images();
    }

    // Documentos ya capturados en step 1 — usar las fotos del OCR
    this.beneficiarioIsRepresentante = true;
    this.beneficiarioFinalForm.get('documento_frontal')?.clearValidators();
    this.beneficiarioFinalForm.get('documento_frontal')?.setValue('from_step1');
    this.beneficiarioFinalForm.get('documento_reverso')?.clearValidators();
    this.beneficiarioFinalForm.get('documento_reverso')?.setValue('from_step1');
    this.beneficiarioFinalForm.get('documento_frontal')?.updateValueAndValidity();
    this.beneficiarioFinalForm.get('documento_reverso')?.updateValueAndValidity();
    this.cdr.detectChanges();
  }

  /**
   * Recargar imágenes del step 1 si se perdieron por recarga de página.
   * Carga desde S3 usando las keys guardadas en data4.
   */
  private async reloadStep1Images(): Promise<void> {
    // Cargar desde S3 usando las keys guardadas
    try {
      if (!this.documentFrontImage && this.savedS3Keys.documentFront) {
        const res = await this.companyService.getDocument(this.savedS3Keys.documentFront);
        if (res?.data?.base64 || res?.base64) {
          const b64 = res.data?.base64 || res.base64;
          this.documentFrontImage = b64.startsWith('data:') ? b64 : `data:image/jpeg;base64,${b64}`;
        }
      }
      if (!this.documentBackImage && this.savedS3Keys.documentBack) {
        const res = await this.companyService.getDocument(this.savedS3Keys.documentBack);
        if (res?.data?.base64 || res?.base64) {
          const b64 = res.data?.base64 || res.base64;
          this.documentBackImage = b64.startsWith('data:') ? b64 : `data:image/jpeg;base64,${b64}`;
        }
      }
      if (!this.selfieImage && this.savedS3Keys.selfie) {
        const res = await this.companyService.getDocument(this.savedS3Keys.selfie);
        if (res?.data?.base64 || res?.base64) {
          const b64 = res.data?.base64 || res.base64;
          this.selfieImage = b64.startsWith('data:') ? b64 : `data:image/jpeg;base64,${b64}`;
        }
      }
      if (this.documentFrontImage || this.documentBackImage || this.selfieImage) {
      }
    } catch (err) {
    }
  }

  async submitFinalStep(): Promise<void> {
    this.ocrLoading = true;
    try {
      // No tocar data4 aquí — los s3Keys y campos OCR ya fueron guardados en submitStep1.
      // Si reescribimos data4, perdemos los s3Keys reales (selfie, documentFront, documentBack).
      await this.submitStepAndAdvance(
        7,
        { confirmed: true, confirmedAt: new Date().toISOString() },
        this.userId || ''
      );
    } catch (error: any) {
      this.showError(error?.message || 'Error al enviar la solicitud. Intenta nuevamente.');
    } finally {
      this.ocrLoading = false;
    }
  }
  onPepToggle2(): void {
    const currentValue = this.beneficiarioFinalForm2.get('es_pep')?.value;
    this.beneficiarioFinalForm2.get('es_pep')?.setValue(!currentValue);
  }
  onPepToggle3(): void {
    const currentValue = this.representantesLegalesForm.get('es_pep')?.value;
    this.representantesLegalesForm.get('es_pep')?.setValue(!currentValue);
  }
  onPepToggle4(): void {
    const currentValue = this.controlEfectivoForm.get('es_pep')?.value;
    this.controlEfectivoForm.get('es_pep')?.setValue(!currentValue);
  }
  onCountryChange(event: Event, target: string): void {
    const selectedCountry = (event.target as HTMLSelectElement).value;
    if (target === 'nacionalidad') {
      this.representanteLegalForm.get('nacionalidad')?.setValue(selectedCountry);
    }
    if (target === 'nacionalidad2') {
      this.beneficiarioFinalForm.get('nacionalidad')?.setValue(selectedCountry);
    }
    if (target === 'paisDir') {
      this.representanteLegalForm
        .get('pais_direccion')
        ?.setValue(selectedCountry);
      this.beneficiarioFinalForm
        .get('pais_direccion')
        ?.setValue(selectedCountry);
    }
    if (target === 'nacionalidad3') {
      this.beneficiarioFinalForm2
        .get('nacionalidad')
        ?.setValue(selectedCountry);
    }
    if (target === 'nacionalidad4') {
      this.representantesLegalesForm
        .get('nacionalidad')
        ?.setValue(selectedCountry);
    }
    if (target === 'nacionalidad5') {
      this.controlEfectivoForm.get('nacionalidad')?.setValue(selectedCountry);
    }
    if (target === 'paisDir2') {
      this.beneficiarioFinalForm2
        .get('pais_direccion')
        ?.setValue(selectedCountry);
    }
    if (target === 'paisDir3') {
      this.representantesLegalesForm
        .get('pais_direccion')
        ?.setValue(selectedCountry);
    }
    if (target === 'paisDir4') {
      this.controlEfectivoForm.get('pais_direccion')?.setValue(selectedCountry);
    }
  }
  getFlagForCountry(countryName: string): string {
    const match = this.countryPhoneOptions.find((c) => c.name === countryName);
    return match ? match.flag : 'chile.png';
  }

  onRutInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^a-zA-Z0-9]/g, '');
    this.representanteLegalForm.get('rut')?.setValue(input.value, { emitEvent: false });
  }
  deleteBeneficiary(id: number): void {
    this.beneficiarios = this.beneficiarios.filter((b) => b.id !== id);
  }
  deleteRepresentante(id: number): void {
    this.representantes = this.representantes.filter((b) => b.id !== id);
  }
  deleteControlEfectivo(id: number): void {
    this.controlEfectivo = this.controlEfectivo.filter((b) => b.id !== id);
  }

  addBeneficiary(): void {
    this.openBeneficiaryForm = true;
  }

  async addNewBeneficiary() {
    if (this.beneficiarioFinalForm2.valid) {
      const newBeneficiary = {
        ...this.beneficiarioFinalForm2.value,
        id: this.beneficiarios.length + 1,
      };
      this.beneficiarios.push(newBeneficiary);

      // Upload documentos del beneficiario adicional
      const rut = this.beneficiarioFinalForm2.get('rut')?.value || '';
      const uploads = [
        { file: this.documento_frontal2, name: `documento_frontal${rut}BeneficiarioFinal` },
        { file: this.documento_reverso2, name: `documento_reverso${rut}BeneficiarioFinal` },
      ];
      for (const { file, name } of uploads) {
        if (!file) continue;
        try {
          const base64 = await this.convertFileToBase64(file);
          await this.companyService.sendFile({
            name,
            base64,
            id: this.user.cognitoId,
          });
        } catch (error) {
        }
      }

      this.beneficiarioFinalForm2.reset();
      this.documento_frontal2 = null;
      this.documento_reverso2 = null;
      this.openBeneficiaryForm = false;
    }
  }
  addControlEfectivo(): void {
    this.openControlEfectivo = true;
  }
  addRepresentanteLegal(): void {
    this.openRepresentanteForm = true;
  }

  /**
   * Auto-poblar representantes legales y control efectivo al entrar al step 5.
   * - Representantes: agrega automáticamente el representante legal del paso 1 (OCR).
   * - Control efectivo: agrega automáticamente el representante legal + todos los beneficiarios.
   */
  autoPopulateStep5(): void {
    const repLegalData = this.representanteLegalForm.getRawValue();

    // Solo auto-poblar si tenemos datos del representante legal
    if (!repLegalData.nombres && !repLegalData.apellidos) return;

    // 1. Auto-agregar representante legal a la lista de representantes
    const repLegalAlreadyInRepresentantes = this.representantes.some(
      (rep) => rep.rut === repLegalData.rut
    );
    if (!repLegalAlreadyInRepresentantes) {
      this.representantes.push({
        ...repLegalData,
        id: this.representantes.length + 1,
        _autoPopulated: true,
      });
    }

    // 2. Auto-agregar representante legal + beneficiarios a control efectivo
    const repLegalAlreadyInControl = this.controlEfectivo.some(
      (c) => c.rut === repLegalData.rut
    );
    if (!repLegalAlreadyInControl) {
      this.controlEfectivo.push({
        ...repLegalData,
        id: this.controlEfectivo.length + 1,
        _autoPopulated: true,
      });
    }

    for (const beneficiario of this.beneficiarios) {
      const alreadyInControl = this.controlEfectivo.some(
        (c) => c.rut === beneficiario.rut
      );
      if (!alreadyInControl) {
        this.controlEfectivo.push({
          ...beneficiario,
          id: this.controlEfectivo.length + 1,
          _autoPopulated: true,
        });
      }
    }
  }
  async addNewRepresentante() {
    if (this.representantesLegalesForm.valid) {
      const newRepresentante = {
        ...this.representantesLegalesForm.value,
        id: this.beneficiarios.length + 1,
      };
      this.representantes.push(newRepresentante);
      await this.uploadRepresentanteFiles();
      //EN ESTE PUNTO NO SE HAN GUARDADO LOS DOCUMENTOS EN S3
      this.representantesLegalesForm.reset();
      this.documento_frontal3 = null;
      this.documento_reverso3 = null;
      this.openRepresentanteForm = false;
    }
  }
  async addNewControlEfectivo() {
    if (this.controlEfectivoForm.valid) {
      const newControl = {
        ...this.controlEfectivoForm.value,
        id: this.controlEfectivo.length + 1,
      };
      this.controlEfectivo.push(newControl);
      await this.uploadControlEfectivoFiles();
      this.controlEfectivoForm.reset();
      this.documento_frontal4 = null;
      this.documento_reverso4 = null;
      this.openControlEfectivo = false;
    }
  }
  addRepresentanteFromBeneficiario(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const idx = Number(target.value);
    if (!isNaN(idx) && this.beneficiarios[idx]) {
      const selectedBeneficiario = this.beneficiarios[idx];

      const alreadyExists = this.representantes.some(
        (rep) => rep.rut === selectedBeneficiario.rut
      );
      if (alreadyExists) return;

      this.representantes.push({ ...selectedBeneficiario });
    }
  }
  addControlEfectivoFromBeneficiario(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const idx = Number(target.value);
    if (!isNaN(idx) && this.beneficiarios[idx]) {
      const selectedBeneficiario = this.beneficiarios[idx];

      const alreadyExists = this.controlEfectivo.some(
        (rep) => rep.rut === selectedBeneficiario.rut
      );
      if (alreadyExists) return;

      this.controlEfectivo.push({ ...selectedBeneficiario });
    }
  }
  onCancel() {
    this.beneficiarioFinalForm2.reset();
    this.documento_frontal2 = null;
    this.documento_reverso2 = null;
    this.openBeneficiaryForm = false;
  }
  onCancel2() {
    this.representantesLegalesForm.reset();
    this.documento_frontal3 = null;
    this.documento_reverso3 = null;
    this.openRepresentanteForm = false;
  }
  onCancel3() {
    this.controlEfectivoForm.reset();
    this.documento_frontal4 = null;
    this.documento_reverso4 = null;
    this.openControlEfectivo = false;
  }
  onTipoEmpresaChange(): void {
    const tipo = this.documentacionForm.get('tipoEmpresa')?.value;

    const requiredDocs: string[] =
      tipo === 'Sociedad de Responsabilidad Limitada' ||
      tipo === 'Empresa Individual de Responsabilidad Limitada' ||
      tipo === 'Sociedad por Acciones'
        ? ['escritura', 'vigencia', 'carpetaTributaria']
        : [
            'escritura',
            'vigencia',
            'carpetaTributaria',
            'conservador',
            'diarioOficial',
          ];

    this.documentosRequeridos.forEach((doc) => {
      const control = this.documentacionForm.get(doc.control);
      if (control && !requiredDocs.includes(doc.control)) {
        control.clearValidators();
        control.setValue(null);
        control.markAsUntouched();
        control.updateValueAndValidity();
        if (this.filePreviews[doc.control]) {
          this.filePreviews[doc.control] = null;
        }
      }
    });

    Object.keys(this.filePreviews).forEach((key) => {
      if (!requiredDocs.includes(key) && !key.startsWith('extra_')) {
        this.filePreviews[key] = null;
      }
    });

    this.documentosRequeridos = [
      ...this.documentosBase.filter((doc) =>
        requiredDocs.includes(doc.control)
      ),
      ...this.documentosRequeridos.filter((doc) =>
        doc.control.startsWith('extra_')
      ),
    ];

    requiredDocs.forEach((key) => {
      const control = this.documentacionForm.get(key);
      if (control) {
        control.setValidators(Validators.required);
        control.updateValueAndValidity();
      }
    });

    this.cdr.detectChanges();
  }

  get requiredFields(): string[] {
    const tipo = this.documentacionForm.get('tipoEmpresa')?.value;

    const baseFields =
      tipo === 'Sociedad de Responsabilidad Limitada' ||
      tipo === 'Empresa Individual de Responsabilidad Limitada' ||
      tipo === 'Sociedad por Acciones'
        ? ['escritura', 'vigencia', 'carpetaTributaria']
        : [
            'escritura',
            'vigencia',
            'carpetaTributaria',
            'conservador',
            'diarioOficial',
          ];

    const extraFields = Object.keys(this.documentacionForm.controls).filter(
      (key) => key.startsWith('extra_')
    );

    return [...baseFields, ...extraFields];
  }

  setRequired(fields: string[]): void {
    fields.forEach((field) => {
      const control = this.documentacionForm.get(field);
      if (control) {
        control.setValidators(Validators.required);
        control.updateValueAndValidity();
      }
    });
  }

  removeFile(control: string): void {
    this.filePreviews[control] = null;
    this.documentacionForm.get(control)?.reset();

    const inputToReset = this.fileInputs.find(
      (el) => el.nativeElement.getAttribute('formControlName') === control
    );
    if (inputToReset) {
      inputToReset.nativeElement.value = '';
    }
  }

  removeExtraField(control: string) {
    this.documentacionForm.removeControl(control);
    delete this.filePreviews[control];

    const index = this.documentosRequeridos.findIndex(
      (d) => d.control === control
    );
    if (index !== -1) {
      this.documentosRequeridos.splice(index, 1);
    }

    this.cdr.detectChanges();
  }

  onFileUpload(event: Event, control: string): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      this.filePreviews[control] = reader.result as string;
      this.documentacionForm.get(control)?.setValue(file);
      this.documentacionForm.get(control)?.markAsTouched();
      this.documentacionForm.get(control)?.updateValueAndValidity();
    };

    if (file.type.startsWith('image/') || file.type === 'application/pdf') {
      reader.readAsDataURL(file);
    }
  }
  isImage(dataUrl: string | null): boolean {
    return !!dataUrl && dataUrl.startsWith('data:image/');
  }

  isPdf(dataUrl: string | null): boolean {
    return !!dataUrl && dataUrl.startsWith('data:application/pdf');
  }

  addExtraField() {
    const extraControlName = `extra_${this.extraFieldCounter++}`;

    this.documentacionForm.addControl(extraControlName, new FormControl(null));
    this.documentosRequeridos.push({
      label: 'Documento adicional ' + this.extraFieldCounter,
      control: extraControlName,
    });

    this.filePreviews[extraControlName] = '';

    this.cdr.detectChanges();
  }

  triggerSelfieUpload() {
    this.selfieInput.nativeElement.click();
  }

  onSelfieSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      this.selectedSelfie = file;
      this.selfieFormGroup.get('selfie')?.setValue(file);

      const reader = new FileReader();
      reader.onload = () => {
        this.selectedSelfiePreview = reader.result;
      };
      reader.readAsDataURL(file);
    }
  }

  private generateCode(length: number): string {
    const chars =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * chars.length);
      result += chars[randomIndex];
    }
    return result;
  }

  // ==================== CAMERA ====================

  async openCameraModal(event?: Event): Promise<void> {
    if (event) event.preventDefault();

    this.ocrLoading = true;
    this.showCameraModal = true;
    this.isReadyToCapture = false;

    setTimeout(async () => {
      if (this.videoElement) {
        if (this.cameraPurpose === 'selfie') {
          await this.initSelfieCamera();
        } else {
          await this.initDocumentCamera();
        }
      }
      this.ocrLoading = false;
    }, 300);
  }

  closeCameraModal(): void {
    this.showCameraModal = false;
    this.cleanupCamera();
  }

  private cleanupCamera(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    const video = this.videoElement?.nativeElement;
    if (video) {
      video.srcObject = null;
      video.load();
    }
  }

  private async initDocumentCamera(): Promise<void> {
    const video = this.videoElement.nativeElement as HTMLVideoElement;
    const configAttempts = ['highEnd', 'midRange', 'lowEnd'] as const;

    for (const configType of configAttempts) {
      try {
        const constraints = await this.buildDocumentCameraConstraints(configType);
        this.stream = await navigator.mediaDevices.getUserMedia(constraints);

        video.srcObject = this.stream;
        video.setAttribute('playsinline', 'true');
        video.setAttribute('autoplay', 'true');
        video.muted = true;

        await video.play();
        await this.applyOptimalCameraSettings(this.stream);

        this.isReadyToCapture = true;
        return;
      } catch (err) {
      }
    }

    this.showError('No se pudo acceder a la camara. Verifica los permisos.');
    this.closeCameraModal();
  }

  private async buildDocumentCameraConstraints(
    configType: 'highEnd' | 'midRange' | 'lowEnd'
  ): Promise<MediaStreamConstraints> {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isMobile = isIOS || isAndroid;

    if (isIOS && configType === 'highEnd') {
      try {
        const tempStream = await navigator.mediaDevices.getUserMedia({ video: true });
        tempStream.getTracks().forEach(t => t.stop());
      } catch (e) { /* ignore */ }
    }

    const config = CAMERA_CONFIGS[configType];
    const videoConstraints: MediaTrackConstraints = {
      ...config,
      facingMode: isMobile ? 'environment' : 'user',
    };

    if (isMobile && configType === 'highEnd') {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cameras = devices.filter(d => d.kind === 'videoinput');
        const backCamera = cameras.find(c => /back|rear|environment|0/i.test(c.label));
        if (backCamera?.deviceId) {
          videoConstraints.deviceId = { exact: backCamera.deviceId };
        }
      } catch (e) { /* use facingMode fallback */ }
    }

    return { video: videoConstraints, audio: false };
  }

  private async applyOptimalCameraSettings(stream: MediaStream): Promise<void> {
    try {
      const [track] = stream.getVideoTracks();
      const capabilities = track.getCapabilities() as any;
      const settings: MediaTrackConstraintSet[] = [];

      if (capabilities.focusMode?.includes('continuous')) {
        settings.push({ focusMode: 'continuous' } as any);
      } else if (capabilities.focusMode?.includes('auto')) {
        settings.push({ focusMode: 'auto' } as any);
      }
      if (capabilities.exposureMode?.includes('continuous')) {
        settings.push({ exposureMode: 'continuous' } as any);
      }
      if (capabilities.whiteBalanceMode?.includes('continuous')) {
        settings.push({ whiteBalanceMode: 'continuous' } as any);
      }
      if (capabilities.exposureCompensation) {
        const targetExposure = Math.min(0.5, capabilities.exposureCompensation.max);
        if (targetExposure >= capabilities.exposureCompensation.min) {
          settings.push({ exposureCompensation: targetExposure } as any);
        }
      }
      if (capabilities.zoom?.min) {
        settings.push({ zoom: capabilities.zoom.min } as any);
      }
      if (settings.length > 0) {
        await track.applyConstraints({ advanced: settings });
      }
    } catch (err) {
    }
  }

  private async initSelfieCamera(): Promise<void> {
    const video = this.videoElement.nativeElement as HTMLVideoElement;
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: SELFIE_CAMERA_CONFIG,
        audio: false,
      });

      video.srcObject = this.stream;
      video.setAttribute('playsinline', 'true');
      video.setAttribute('autoplay', 'true');
      video.muted = true;
      await video.play();

      try {
        const [track] = this.stream.getVideoTracks();
        const capabilities = track.getCapabilities() as any;
        const settings: MediaTrackConstraintSet[] = [];
        if (capabilities.focusMode?.includes('continuous')) {
          settings.push({ focusMode: 'continuous' } as any);
        }
        if (capabilities.exposureMode?.includes('continuous')) {
          settings.push({ exposureMode: 'continuous' } as any);
        }
        if (settings.length > 0) {
          await track.applyConstraints({ advanced: settings });
        }
      } catch (err) { /* non-critical */ }

      this.isReadyToCapture = true;
    } catch (err) {
      this.showError('No se pudo acceder a la camara. Verifica los permisos.');
      this.closeCameraModal();
    }
  }

  // ==================== DOCUMENT CAPTURE ====================

  captureDocument(): void {
    const video = this.videoElement.nativeElement as HTMLVideoElement;
    if (!video || video.videoWidth === 0) {
      this.showError('La camara no esta lista. Intenta de nuevo.');
      return;
    }

    try {
      const base64 = this.captureDocumentArea(video);
      this.currentImage = base64;
      this.rotationAngle = 0;
      this.closeCameraModal();
    } catch (err) {
      this.showError('Error al capturar imagen. Intenta de nuevo.');
    }
  }

  private captureDocumentArea(video: HTMLVideoElement): string {
    const { videoWidth, videoHeight } = video;
    const frameWidthPercent = 0.85;
    const frameWidth = Math.round(videoWidth * frameWidthPercent);
    const frameHeight = Math.round(frameWidth / CAPTURE_CONFIG.documentAspectRatio);
    const startX = Math.round((videoWidth - frameWidth) / 2);
    const startY = Math.round((videoHeight - frameHeight) / 2);

    const canvas = document.createElement('canvas');
    let targetWidth = frameWidth;
    let targetHeight = frameHeight;

    if (targetWidth > CAPTURE_CONFIG.targetWidth) {
      const scale = CAPTURE_CONFIG.targetWidth / targetWidth;
      targetWidth = CAPTURE_CONFIG.targetWidth;
      targetHeight = Math.round(frameHeight * scale);
    }

    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No se pudo crear contexto de canvas');

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(video, startX, startY, frameWidth, frameHeight, 0, 0, targetWidth, targetHeight);

    return this.compressToTargetSize(canvas);
  }

  captureSelfie(): void {
    const video = this.videoElement.nativeElement as HTMLVideoElement;
    if (!video || video.videoWidth === 0) {
      this.showError('La camara no esta lista. Intenta de nuevo.');
      return;
    }

    try {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('No se pudo crear contexto de canvas');
      // Mirror the selfie horizontally (match the scaleX(-1) applied in CSS)
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, 0, 0);
      this.currentImage = this.compressToTargetSize(canvas);
      this.closeCameraModal();
    } catch (err) {
      this.showError('Error al capturar selfie. Intenta de nuevo.');
    }
  }

  private compressToTargetSize(canvas: HTMLCanvasElement): string {
    let quality = CAPTURE_CONFIG.quality;
    let result = canvas.toDataURL(CAPTURE_CONFIG.format, quality);

    while (
      this.getFileSizeMB(result) > CAPTURE_CONFIG.maxFileSizeMB &&
      quality > CAPTURE_CONFIG.minQuality
    ) {
      quality -= CAPTURE_CONFIG.qualityStep;
      result = canvas.toDataURL(CAPTURE_CONFIG.format, quality);
    }

    if (this.getFileSizeMB(result) > CAPTURE_CONFIG.maxFileSizeMB) {
      const scale = 0.75;
      const smallCanvas = document.createElement('canvas');
      smallCanvas.width = Math.round(canvas.width * scale);
      smallCanvas.height = Math.round(canvas.height * scale);
      const ctx = smallCanvas.getContext('2d');
      if (ctx) {
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(canvas, 0, 0, smallCanvas.width, smallCanvas.height);
        return smallCanvas.toDataURL(CAPTURE_CONFIG.format, quality);
      }
    }

    return result;
  }

  private getFileSizeMB(base64: string): number {
    const base64Data = base64.split(',')[1] || '';
    return (base64Data.length * 0.75) / (1024 * 1024);
  }

  // ==================== ROTATION ====================

  rotateLeft(): void {
    this.rotationAngle = (this.rotationAngle - 90 + 360) % 360;
  }

  rotateRight(): void {
    this.rotationAngle = (this.rotationAngle + 90) % 360;
  }

  retakeImage(event?: Event): void {
    if (event) event.preventDefault();
    this.currentImage = '';
    this.rotationAngle = 0;
    this.openCameraModal();
  }

  private async ensureOptimalSize(base64: string): Promise<string> {
    const sizeMB = this.getFileSizeMB(base64);
    if (sizeMB <= CAPTURE_CONFIG.maxFileSizeMB) return base64;

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const scale = Math.min(1, CAPTURE_CONFIG.targetWidth / img.width);
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('No se pudo crear contexto')); return; }
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(this.compressToTargetSize(canvas));
      };
      img.onerror = () => reject(new Error('Error al cargar imagen'));
      img.src = base64;
    });
  }

  private async applyRotationAndOptimize(base64: string, angle: number): Promise<string> {
    if (angle === 0) return this.ensureOptimalSize(base64);

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('No se pudo crear contexto')); return; }

        if (angle % 180 === 0) {
          canvas.width = img.width;
          canvas.height = img.height;
        } else {
          canvas.width = img.height;
          canvas.height = img.width;
        }

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((angle * Math.PI) / 180);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);

        resolve(this.compressToTargetSize(canvas));
      };
      img.onerror = () => reject(new Error('Error al cargar imagen'));
      img.src = base64;
    });
  }

  // ==================== CONFIRM FRONT ====================

  async confirmFrontDocument(): Promise<void> {
    if (!this.currentImage) {
      return;
    }
    this.ocrLoading = true;

    try {
      const finalImage = await this.applyRotationAndOptimize(this.currentImage, this.rotationAngle);
      this.documentFrontImage = finalImage;

      // Send to OCR Chile
      const file = this.base64ToFile(finalImage, 'document-front.jpg');
      const registro: OcrRegistroData = {
        cognitoId: this.user?.cognitoId || '',
        tipo: 'EMPRESA',
        email: this.user?.email || undefined,
        whatsapp: this.user?.prefijoTelefono && this.user?.telefono
          ? `${this.user.prefijoTelefono}${this.user.telefono}`
          : undefined,
        ip: this.geolocationService.cachedGeoData?.ip || undefined,
      };

      const ocrResult = await this.kycService.ocrChile(file, registro);
      this.validateOcrResponse(ocrResult);

      // Store raw OCR response for backend submission
      this.ocrRawResponse = ocrResult;

      // Map OCR data for form pre-fill
      this.ocrData = this.mapOcrResult(ocrResult);
      this.fotoPersonaFromOcr = ocrResult.fotoPersona || '';

      // El documento del representante legal no se chequea acá: ser representante
      // es un rol ortogonal a ser cliente persona o pagador, y el RUT de la
      // empresa lo revisan el e-RUT y la consolidación. El chequeo anterior solo
      // bloqueaba si el back decía `registeredAs: 'empresa'`, cosa que ya no
      // devuelve (R5), así que no frenaba nunca.

      // Pre-fill form
      this.representanteLegalForm.patchValue({
        nombres: this.ocrData.firstName || '',
        apellidos: this.ocrData.lastName || '',
        rut: this.ocrData.documentNumber || '',
        nacionalidad: this.ocrData.nationality || '',
      });
      if (this.ocrData.expirationDate) {
        this.representanteLegalForm.patchValue({
          fecha_vencimiento_dni: this.ocrData.expirationDate,
        });
      }

      // Advance to back capture
      this.currentImage = '';
      this.rotationAngle = 0;
      this.step1SubStep = 'capture-back';
    } catch (err: any) {
      const msg = this.handleOcrError(err);
      this.showError(msg);
      this.currentImage = '';
    } finally {
      this.ocrLoading = false;
    }
  }

  // ==================== CONFIRM BACK ====================

  async confirmBackDocument(): Promise<void> {
    if (!this.currentImage) return;
    this.ocrLoading = true;

    try {
      const finalImage = await this.applyRotationAndOptimize(this.currentImage, this.rotationAngle);
      this.documentBackImage = finalImage;

      // Advance to selfie
      this.currentImage = '';
      this.rotationAngle = 0;
      this.step1SubStep = 'capture-selfie';
    } catch (err) {
      this.showError('Error procesando imagen. Intenta de nuevo.');
      this.currentImage = '';
    } finally {
      this.ocrLoading = false;
    }
  }

  // ==================== CONFIRM SELFIE ====================

  async confirmSelfie(): Promise<void> {
    if (!this.currentImage) return;
    this.faceVerificationStatus = 'analyzing';
    this.ocrLoading = true;

    try {
      this.selfieImage = this.currentImage;

      if (!this.fotoPersonaFromOcr) {
        throw { code: 'NO_FACE_DETECTED_TARGET', message: 'No se encontro la foto del documento' };
      }

      const selfieFile = this.base64ToFile(this.selfieImage, 'selfie.jpg');
      const result = await this.kycService.verifyFace(
        selfieFile,
        this.fotoPersonaFromOcr,
        SIMILARITY_THRESHOLD
      );

      this.faceSimilarity = result.similarity || 0;

      // El liveness lo decide el backend: si bloqueo, este bloque no se ejecuta (el
      // endpoint responde 400). Las observaciones NO se loguean en el navegador a
      // proposito: los motivos revelan que senal detecto el ataque, y exponerlos en
      // consola le daria al atacante el mapa para evadirlos. El backend ya los
      // registra del lado servidor.

      if (!result.sourceFaceDetected) {
        this.registerFailedFaceAttempt(
          'No pudimos detectar tu rostro. Asegurate de estar bien iluminado y mira de frente a la camara.'
        );
        return;
      }

      if (!result.targetFaceDetected) {
        // No es culpa del usuario en este paso: el problema está en la foto del
        // documento, así que no le consume intentos de selfie.
        this.faceVerificationStatus = 'error';
        this.faceVerificationMessage = 'No se pudo leer la foto del documento. Vuelve a capturar el frente.';
        return;
      }

      // Puerta única: la coincidencia con el documento debe llegar al umbral.
      if (result.similarity >= SIMILARITY_THRESHOLD) {
        this.faceVerificationStatus = 'success';
        this.faceVerificationMessage = 'Verificacion facial exitosa';
        // Auto-advance to form after delay
        setTimeout(() => {
          this.currentImage = '';
          this.step1SubStep = 'form';
          this.cdr.detectChanges();
        }, 2000);
      } else {
        this.registerFailedFaceAttempt(
          `Tu rostro no coincide lo suficiente con la foto del documento ` +
            `(${result.similarity.toFixed(0)}% de ${SIMILARITY_THRESHOLD}% requerido). ` +
            `Intentalo de nuevo con buena luz y sin lentes.`
        );
      }
    } catch (error: any) {
      const errorCode = error?.code || error?.error?.error?.code || '';
      const backendMessage = error?.message;
      const message = PREFER_BACKEND_MESSAGE.has(errorCode)
        ? backendMessage || OCR_ERROR_MESSAGES[errorCode]
        : OCR_ERROR_MESSAGES[errorCode] || backendMessage;

      // Solo consumen intentos los rechazos que el usuario puede corregir; un error
      // del servicio (AWS caido, datos invalidos) no.
      if (errorCode === 'SELFIE_SPOOF_DETECTED' || errorCode === 'NO_FACE_DETECTED_SOURCE') {
        this.registerFailedFaceAttempt(
          message || 'No pudimos verificar la selfie. Intenta de nuevo.'
        );
      } else {
        this.faceVerificationStatus = 'error';
        this.faceVerificationMessage =
          message || 'Error en la verificacion facial. Intenta de nuevo.';
      }
    } finally {
      this.ocrLoading = false;
    }
  }

  /**
   * Cuenta el intento fallido y, al llegar al maximo, ofrece ayuda por WhatsApp en
   * lugar de dejar al usuario en un bucle de "intentar de nuevo".
   */
  private registerFailedFaceAttempt(message: string): void {
    this.faceAttempts += 1;
    this.faceVerificationStatus = 'error';
    this.faceVerificationMessage = message;
  }

  /** true cuando se agotaron los intentos y hay que ofrecer soporte. */
  get faceAttemptsExhausted(): boolean {
    return this.faceAttempts >= MAX_VERIFICATION_ATTEMPTS;
  }

  /** Abre WhatsApp de soporte con un mensaje precargado. */
  contactSupport(): void {
    const text = encodeURIComponent(
      'Hola, no pude completar la verificacion de identidad en el registro de empresa y necesito ayuda.'
    );
    window.open(`https://wa.me/${SUPPORT_WHATSAPP}?text=${text}`, '_blank');
  }

  retakeSelfie(): void {
    this.selfieImage = '';
    this.currentImage = '';
    this.faceVerificationStatus = 'idle';
    this.faceVerificationMessage = '';
    this.faceSimilarity = 0;
  }

  // ==================== SUBMIT STEP 1 ====================

  async submitStep1(): Promise<void> {
    if (!this.representanteLegalForm.valid) return;
    this.ocrLoading = true;

    // Determinar si tenemos datos nuevos de la sesión actual (imágenes/OCR)
    // o si es una recarga de página (solo datos del formulario desde data1)
    const hasNewCaptureData = !!(this.documentFrontImage || this.documentBackImage || this.selfieImage || this.ocrRawResponse);

    try {
      // 1. Build step 1 payload (todos los campos del formulario incluyendo OCR)
      const formValues = this.representanteLegalForm.getRawValue();
      const step1Data = {
        nombres: formValues.nombres,
        apellidos: formValues.apellidos,
        rut: formValues.rut,
        nacionalidad: formValues.nacionalidad,
        direccion_particular: formValues.direccion_particular,
        tipo_departamento: formValues.tipo_departamento,
        comuna: formValues.comuna,
        ciudad: formValues.ciudad,
        pais_direccion: formValues.pais_direccion,
        porcentaje_participacion: formValues.porcentaje_participacion,
        fecha_vencimiento_dni: formValues.fecha_vencimiento_dni,
        es_pep: formValues.es_pep,
        pep_funcion: formValues.pep_funcion,
        pep_institucion: formValues.pep_institucion,
        pep_fecha_inicio: formValues.pep_fecha_inicio,
        pep_fecha_termino: formValues.pep_fecha_termino,
        _format: 'new_ocr',
        _processedAt: new Date().toISOString(),
      };

      // 2. Siempre guardar data1 (campos del formulario)
      await this.companyService.addDataToRegister(1, step1Data, this.userId || '');

      // 3. Solo actualizar data4 si tenemos datos nuevos de captura (no sobrescribir con vacíos en recarga)
      if (hasNewCaptureData) {
        let frontKey = '';
        let backKey = '';
        let selfieKey = '';

        if (this.documentFrontImage) {
          const frontData = await this.companyService.sendFile({
            name: 'documento_frontal_representante',
            base64: this.documentFrontImage,
            id: this.user?.cognitoId,
          });
          frontKey = frontData.key;
        }

        if (this.documentBackImage) {
          const backData = await this.companyService.sendFile({
            name: 'documento_reverso_representante',
            base64: this.documentBackImage,
            id: this.user?.cognitoId,
          });
          backKey = backData.key;
        }

        if (this.selfieImage) {
          const selfieData = await this.companyService.sendFile({
            name: 'selfie_representante',
            base64: this.selfieImage,
            id: this.user?.cognitoId,
          });
          selfieKey = selfieData.key;
        }

        const step4Data = {
          s3Keys: {
            selfie: selfieKey,
            documentFront: frontKey,
            documentBack: backKey,
          },
          ocr: {
            campos: this.ocrRawResponse?.campos || {},
            validacion: this.ocrRawResponse?.validacion || {},
            verificacionSanciones: this.ocrRawResponse?.verificacionSanciones || null,
            fotoPersona: this.ocrRawResponse?.fotoPersona || '',
          },
          facialVerification: {
            isMatch: this.faceSimilarity >= SIMILARITY_THRESHOLD,
            similarity: this.faceSimilarity / 100,
            threshold: SIMILARITY_THRESHOLD / 100,
          },
        };

        await this.companyService.addDataToRegister(4, step4Data, this.userId || '');
      } else {
        // Recarga de página: no tenemos imágenes ni OCR raw en memoria.
        // NO tocar data4 aquí — los s3Keys reales ya están en la BD.
        // Si reescribimos data4, perdemos selfie/documentFront/documentBack.
      }

      // 4. Advance visual step
      this.goToStep(this.currentStep + 1);
      this.errorMessage = '';
    } catch (error: any) {
      this.showError(error?.message || 'Error al guardar los datos. Intenta nuevamente.');
    } finally {
      this.ocrLoading = false;
    }
  }

  // ==================== OCR HELPERS ====================

  private validateOcrResponse(response: any): void {
    if (response.verificacionSanciones?.hasMatches) {
      const riskLevel = response.verificacionSanciones.riskLevel;
      if (riskLevel === 'CRITICAL' || riskLevel === 'HIGH') {
        throw new Error('No es posible continuar con el registro. Contacte a soporte.');
      }
    }

    // La edad va antes que camposCompletos: el KYC también lo deja en false para
    // un menor, y el mensaje de "Faltan datos" no le explicaba nada al usuario.
    const motivoEdad = motivoEdadEnOcr(response, true);
    if (motivoEdad) {
      throw new Error(motivoEdad);
    }

    const validacion = response.validacion;
    if (validacion?.estaVencido) {
      throw new Error('El documento esta vencido. Debes renovarlo para continuar.');
    }
    if ('runValido' in validacion && !validacion.runValido) {
      throw new Error('El RUN del documento no es valido.');
    }
    if (validacion?.confianzaOCR < 70) {
      throw new Error('La imagen no es clara. Toma otra foto con mejor iluminacion.');
    }
    if (validacion && !validacion.camposCompletos) {
      const advertencias = validacion.advertencias?.join(', ') || 'algunos campos';
      throw new Error(`Faltan datos: ${advertencias}. Captura el documento completo.`);
    }
  }

  private handleOcrError(error: any): string {
    const errorCode = error?.error?.error?.code || error?.error?.errorCode || error?.code || 'UNKNOWN';
    if (OCR_ERROR_MESSAGES[errorCode]) return OCR_ERROR_MESSAGES[errorCode];
    return error?.error?.error?.message || error?.error?.message || error?.message || 'Error procesando el documento. Intenta nuevamente.';
  }

  private mapOcrResult(result: any): any {
    const isExtranjero = result.tipoDocumento?.toUpperCase()?.includes('EXTRANJERO');
    return {
      firstName: result.campos?.nombres || '',
      lastName: result.campos?.apellidos || '',
      fullName: `${result.campos?.nombres || ''} ${result.campos?.apellidos || ''}`.trim(),
      documentNumber: result.campos?.run || '',
      documentNumberClean: result.campos?.runSinFormato || '',
      RUN: result.campos?.run || result.campos?.runSinFormato || '',
      runValido: result.validacion?.runValido || false,
      country: 'CHL',
      countryName: 'Chile',
      documentType: result.tipoDocumento || 'Cedula de Identidad',
      isExtranjero,
      confidenceScore: result.validacion?.confianzaOCR || 0,
      nationality: this.mapNationality(result.campos?.nacionalidad) || 'Chile',
      gender: result.campos?.sexo || '',
      fechaNacimientoISO: result.campos?.fechaNacimientoISO || '',
      issueDate: result.campos?.fechaEmisionISO || '',
      expirationDate: result.campos?.fechaVencimientoISO || '',
      diasHastaVencimiento: result.validacion?.diasHastaVencimiento,
      isExpired: result.validacion?.estaVencido || false,
      fotoPersona: result.fotoPersona || '',
      isValid: result.validacion?.runValido && !result.validacion?.estaVencido,
      warnings: result.validacion?.advertencias || [],
      sanctionsCheck: result.verificacionSanciones || null,
      hasSanctionsRisk: result.verificacionSanciones?.hasMatches || false,
    };
  }

  private mapNationality(code: string | undefined): string {
    if (!code) return '';
    const upperCode = code.toUpperCase().trim();
    return NATIONALITY_MAP[upperCode] || code;
  }

  private base64ToFile(base64: string, filename: string): File {
    const arr = base64.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  // ==================== STEP 1 NAVIGATION ====================

  openDocumentCamera(): void {
    this.cameraPurpose = 'document';
    this.openCameraModal();
  }

  openSelfieCamera(): void {
    this.cameraPurpose = 'selfie';
    this.openCameraModal();
  }

  goBackStep1(): void {
    if (this.step1SubStep === 'start-verification') {
      this.step1SubStep = 'intro';
    } else if (this.step1SubStep === 'capture-front') {
      this.step1SubStep = 'start-verification';
      this.currentImage = '';
    } else if (this.step1SubStep === 'capture-back') {
      this.step1SubStep = 'capture-front';
      this.currentImage = this.documentFrontImage || '';
    } else if (this.step1SubStep === 'capture-selfie') {
      this.step1SubStep = 'capture-back';
      this.currentImage = this.documentBackImage || '';
    } else if (this.step1SubStep === 'form') {
      this.step1SubStep = 'capture-selfie';
      this.currentImage = this.selfieImage || '';
    }
    this.rotationAngle = 0;
  }

  private showError(msg: string): void {
    this.errorMessage = msg;
    clearTimeout(this.errorTimeout);
    this.errorTimeout = setTimeout(() => (this.errorMessage = ''), 6000);
  }

  ngOnDestroy(): void {
    this.cleanupCamera();
    this.subscription.unsubscribe();
  }

  redirect(route: string) {
    this.router.navigate([route]);
  }
}
